import { Tree, TreeData, TreeMetadata, TreeTranslation } from '../types/tree';
import { TREES_API_BASE } from './config';

export async function saveTree(name: string, data: TreeData, language: string = 'en'): Promise<string> {
  try {
    const response = await fetch(`${TREES_API_BASE}/api/trees`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ 
        name, 
        data,
        metadata: {
          primaryLanguage: language,
          translations: []
        }
      }),
    });
    
    if (!response.ok) {
      throw new Error('Failed to save tree');
    }
    
    const result = await response.json();
    return result.id;
  } catch (error) {
    console.error('Error saving tree:', error);
    throw error;
  }
}

export async function loadTree(id: string): Promise<Tree> {
  try {
    const response = await fetch(`${TREES_API_BASE}/api/trees/${id}`);
    
    if (!response.ok) {
      throw new Error('Failed to load tree');
    }
    
    return response.json();
  } catch (error) {
    console.error('Error loading tree:', error);
    throw error;
  }
}

export async function listTrees(): Promise<TreeMetadata[]> {
  try {
    const response = await fetch(`${TREES_API_BASE}/api/trees`);
    
    if (!response.ok) {
      throw new Error('Failed to list trees');
    }
    
    return response.json();
  } catch (error) {
    console.error('Error listing trees:', error);
    throw error;
  }
}

export async function deleteTree(id: string): Promise<void> {
  try {
    const response = await fetch(`${TREES_API_BASE}/api/trees/${id}`, {
      method: 'DELETE',
    });
    
    if (!response.ok) {
      throw new Error('Failed to delete tree');
    }
  } catch (error) {
    console.error('Error deleting tree:', error);
    throw error;
  }
}

export async function syncTree(id: string, updates: { name?: string; data?: TreeData; metadata?: Partial<TreeMetadata> }): Promise<void> {
  try {
    console.log('Syncing tree with ID:', id);
    console.log('Request data:', JSON.stringify(updates, null, 2));
    
    const response = await fetch(`${TREES_API_BASE}/api/trees/${id}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(updates),
    });

    if (!response.ok) {
      const errorData = await response.text();
      console.error('Sync failed with status:', response.status);
      console.error('Error response:', errorData);
      try {
        const jsonError = JSON.parse(errorData);
        throw new Error(jsonError.details || jsonError.error || 'Failed to sync tree');
      } catch (e) {
        throw new Error(errorData || 'Failed to sync tree');
      }
    }
  } catch (error) {
    console.error('Error syncing tree:', error);
    throw error;
  }
}

export async function createTranslation(primaryTreeId: string, language: string, name: string): Promise<string> {
  try {
    const response = await fetch(`${TREES_API_BASE}/api/trees/${primaryTreeId}/translations`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ language, name }),
    });
    
    if (!response.ok) {
      const errorData = await response.text();
      try {
        const jsonError = JSON.parse(errorData);
        throw new Error(jsonError.details?.toString() || jsonError.error || 'Failed to create translation');
      } catch (e) {
        throw new Error(errorData || 'Failed to create translation');
      }
    }
    
    const result = await response.json();
    return result.id;
  } catch (error) {
    console.error('Error creating translation:', error);
    throw error;
  }
}

export async function loadTranslation(primaryTreeId: string, language: string): Promise<Tree | null> {
  try {
    const response = await fetch(`${TREES_API_BASE}/api/trees/${primaryTreeId}/translations/${language}`);
    
    if (!response.ok) {
      if (response.status === 404) {
        return null;
      }
      throw new Error('Failed to load translation');
    }
    
    return response.json();
  } catch (error) {
    console.error('Error loading translation:', error);
    throw error;
  }
}

export async function listTranslations(primaryTreeId: string): Promise<TreeTranslation[]> {
  try {
    const response = await fetch(`${TREES_API_BASE}/api/trees/${primaryTreeId}/translations`);
    
    if (!response.ok) {
      throw new Error('Failed to list translations');
    }
    
    return response.json();
  } catch (error) {
    console.error('Error listing translations:', error);
    throw error;
  }
}

export async function uploadTranslation(primaryTreeId: string, translationData: { language: string; name: string; data: TreeData }): Promise<string> {
  try {
    const response = await fetch(`${TREES_API_BASE}/api/trees/${primaryTreeId}/translations`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        language: translationData.language,
        name: translationData.name,
        data: translationData.data
      }),
    });

    if (!response.ok) {
      throw new Error('Failed to upload translation');
    }

    const result = await response.json();
    return result.id;
  } catch (error) {
    console.error('Error uploading translation:', error);
    throw error;
  }
}
